import { Children, isValidElement, type ReactNode } from 'react';

import type { ReferenceLineData } from '../../types/ReferenceLineData';
import type { DataType } from '../../utils/formatters';
import type { YAxisSide } from '../types/YAxisProps';

import { isElement } from './isElement';

/**
 * Per-side Y-axis configuration extracted from chart children.
 */
export interface YAxisSideConfig {
  /** Array of data keys from series bound to this axis */
  seriesDataKeys: string[];
  /** Whether a YAxis component for this side is present */
  hasYAxis: boolean;
  /** Number of ticks (from YAxis numTicks prop, defaults to 5) */
  yAxisNumTicks: number;
  /** Y values that must be in the domain: ReferenceLine points + (left only) ReferenceArea bounds */
  domainInclusionPoints: number[];
  /** ReferenceLine points only — used for boundary padding so lines aren't clipped at edges */
  referenceLinePoints: number[];
  yAxisFallbackMin?: number;
  yAxisFallbackMax?: number;
  yAxisDataType?: DataType;
  yAxisTickFormat?: (value: number) => string;
  domainMin?: number;
  domainMax?: number;
  /**
   * Effective label offset (px). 55 when a label is present and labelOffset is omitted, else 0.
   * Used by useAutoMargin for the right axis.
   */
  yAxisLabelOffset: number;
  /** When true, this side's domain uses stacked max (sum of series per x). */
  useStackedYDomain?: boolean;
}

/**
 * Configuration extracted from YAxis and series children.
 * Used by chart components to calculate Y domain synchronously.
 *
 * Left-axis fields stay at the top level for backward compatibility with ColumnChart.
 * `right` is populated when a right `<YAxis>` or `yAxis="right"` series is present.
 */
export interface YAxisConfig extends YAxisSideConfig {
  /** When true, Y domain should use stacked max (0 to sum of series per x). Used for AreaStackSeries. */
  useStackedYDomain?: boolean;
  /** Independent right-axis config. Absent when the chart has no right axis or right series. */
  right?: YAxisSideConfig;
}

const isYAxisElement = (child: React.ReactElement) => isElement(child, 'YAxis');
const isSeriesElement = (child: React.ReactElement) => isElement(child, 'Series');
const isAreaSeriesElement = (child: React.ReactElement) => isElement(child, 'AreaSeries');
const isAreaStackSeriesElement = (child: React.ReactElement) => isElement(child, 'AreaStackSeries');
const isBarSeriesElement = (child: React.ReactElement) => isElement(child, 'BarSeries');
const isReferenceLineElement = (child: React.ReactElement) => isElement(child, 'ReferenceLine');
const isReferenceAreaElement = (child: React.ReactElement) => isElement(child, 'ReferenceArea');

const DEFAULT_Y_AXIS_LABEL_OFFSET = 55;

interface YAxisElementConfig {
  hasYAxis: boolean;
  numTicks: number;
  fallbackMin?: number;
  fallbackMax?: number;
  dataType?: DataType;
  tickFormat?: (value: number) => string;
  domainMin?: number;
  domainMax?: number;
  yAxisLabelOffset: number;
}

function emptyYAxisElementConfig(): YAxisElementConfig {
  return { hasYAxis: false, numTicks: 5, yAxisLabelOffset: 0 };
}

function uniqueKeys(keys: string[]): string[] {
  return Array.from(new Set(keys));
}

function resolveYAxisSide(orientation?: YAxisSide): YAxisSide {
  return orientation === 'right' ? 'right' : 'left';
}

function resolveSeriesYAxis(yAxis?: YAxisSide): YAxisSide {
  return yAxis === 'right' ? 'right' : 'left';
}

/**
 * Recursively searches children to find left and right YAxis props.
 * Two axes of the same orientation: last one wins.
 *
 * @param defaultNumTicks - Fallback tick count used when a YAxis element is present but has
 *   no explicit numTicks prop. Typically derived from the chart container height.
 *   When no YAxis is present this parameter is ignored — the hardcoded default of 5 is kept
 *   so the Y scale domain calculation is not affected by the container height.
 */
function findYAxisConfigs(
  children: ReactNode,
  defaultNumTicks = 5,
): { left: YAxisElementConfig; right: YAxisElementConfig } {
  let left = emptyYAxisElementConfig();
  let right = emptyYAxisElementConfig();

  const applyProps = (props: Record<string, unknown>): YAxisElementConfig => {
    const numTicks = props.numTicks as number | undefined;
    const label = props.label as string | undefined;
    const labelOffset = props.labelOffset as number | undefined;
    return {
      hasYAxis: true,
      numTicks: numTicks ?? defaultNumTicks,
      fallbackMin: props.fallbackMin !== undefined ? (props.fallbackMin as number) : undefined,
      fallbackMax: props.fallbackMax !== undefined ? (props.fallbackMax as number) : undefined,
      dataType: props.dataType !== undefined ? (props.dataType as DataType) : undefined,
      tickFormat: props.tickFormat as ((value: number) => string) | undefined,
      domainMin: props.domainMin as number | undefined,
      domainMax: props.domainMax as number | undefined,
      yAxisLabelOffset: labelOffset ?? (label ? DEFAULT_Y_AXIS_LABEL_OFFSET : 0),
    };
  };

  const searchChild = (child: ReactNode): void => {
    if (!isValidElement(child)) {
      return;
    }

    if (isYAxisElement(child)) {
      const props = child.props as { orientation?: YAxisSide };
      const applied = applyProps(child.props as Record<string, unknown>);
      if (resolveYAxisSide(props.orientation) === 'right') {
        right = applied;
      } else {
        left = applied;
      }
      return;
    }

    const childProps = child.props as { children?: ReactNode };
    if (childProps.children) {
      Children.forEach(childProps.children, searchChild);
    }
  };

  Children.forEach(children, searchChild);
  return { left, right };
}

/**
 * Recursively extracts dataKeys from Series, AreaSeries, BarSeries, and AreaStackSeries, split by yAxis.
 */
function findSeriesDataKeysByAxis(children: ReactNode): { left: string[]; right: string[] } {
  const left: string[] = [];
  const right: string[] = [];

  const pushKey = (side: YAxisSide, dataKey: string | undefined): void => {
    if (dataKey !== undefined) {
      (side === 'right' ? right : left).push(dataKey);
    }
  };

  const searchChild = (child: ReactNode): void => {
    if (!isValidElement(child)) {
      return;
    }

    if (isSeriesElement(child) || isBarSeriesElement(child)) {
      const props = child.props as { dataKey?: string; yAxis?: YAxisSide };
      pushKey(resolveSeriesYAxis(props.yAxis), props.dataKey);
      return;
    }

    if (isAreaSeriesElement(child)) {
      const props = child.props as {
        lowerBoundDataKey?: string;
        upperBoundDataKey?: string;
        excludeFromYScale?: boolean;
        yAxis?: YAxisSide;
      };
      if (!props.excludeFromYScale) {
        const side = resolveSeriesYAxis(props.yAxis);
        pushKey(side, props.lowerBoundDataKey);
        pushKey(side, props.upperBoundDataKey);
      }
      return;
    }

    if (isAreaStackSeriesElement(child)) {
      const props = child.props as { series?: Array<{ dataKey?: string; yAxis?: YAxisSide }> };
      if (Array.isArray(props.series)) {
        for (const item of props.series) {
          pushKey(resolveSeriesYAxis(item.yAxis), item.dataKey);
        }
      }
      return;
    }

    const childProps = child.props as { children?: ReactNode };
    if (childProps.children) {
      Children.forEach(childProps.children, searchChild);
    }
  };

  Children.forEach(children, searchChild);

  return { left: uniqueKeys(left), right: uniqueKeys(right) };
}

interface HorizontalRefLinePoint {
  point: number;
  yAxis: YAxisSide;
}

/**
 * Recursively extracts horizontal reference line points from ReferenceLine components.
 */
function findHorizontalReferenceLinePointsFromChildren(
  children: ReactNode,
  referenceLineData?: ReferenceLineData[],
): HorizontalRefLinePoint[] {
  const points: HorizontalRefLinePoint[] = [];

  const searchChild = (child: ReactNode): void => {
    if (!isValidElement(child)) {
      return;
    }

    if (isReferenceLineElement(child)) {
      const props = child.props as {
        type?: 'vertical' | 'horizontal';
        point?: number | string;
        referenceKey?: string;
        yAxis?: YAxisSide;
      };

      let resolvedType: 'vertical' | 'horizontal' | undefined = props.type;
      if (props.referenceKey && referenceLineData) {
        const refLineData = referenceLineData.find((d) => d.key === props.referenceKey);
        resolvedType = refLineData?.type ?? props.type;
      }

      if (resolvedType === 'horizontal') {
        const yAxis = resolveSeriesYAxis(props.yAxis);
        if (props.referenceKey && referenceLineData) {
          const refLineData = referenceLineData.find((d) => d.key === props.referenceKey);
          if (typeof refLineData?.point === 'number') {
            points.push({ point: refLineData.point, yAxis });
          } else if (typeof props.point === 'number') {
            points.push({ point: props.point, yAxis });
          }
        } else if (typeof props.point === 'number') {
          points.push({ point: props.point, yAxis });
        }
      }
      return;
    }

    const childProps = child.props as { children?: ReactNode };
    if (childProps.children) {
      Children.forEach(childProps.children, searchChild);
    }
  };

  Children.forEach(children, searchChild);

  return points;
}

function findReferenceAreaBounds(children: ReactNode): number[] {
  const bounds: number[] = [];

  const searchChild = (child: ReactNode): void => {
    if (!isValidElement(child)) {
      return;
    }

    if (isReferenceAreaElement(child)) {
      const props = child.props as { y1?: number; y2?: number };
      if (props.y1 !== undefined) {
        bounds.push(props.y1);
      }
      if (props.y2 !== undefined) {
        bounds.push(props.y2);
      }
      return;
    }

    const childProps = child.props as { children?: ReactNode };
    if (childProps.children) {
      Children.forEach(childProps.children, searchChild);
    }
  };

  Children.forEach(children, searchChild);

  return bounds;
}

function findStackedYDomainByAxis(children: ReactNode): { left: boolean; right: boolean } {
  let left = false;
  let right = false;
  const searchChild = (child: ReactNode): void => {
    if (!isValidElement(child)) {
      return;
    }
    if (isAreaStackSeriesElement(child)) {
      const props = child.props as { series?: Array<{ yAxis?: YAxisSide }> };
      if (!Array.isArray(props.series) || props.series.length === 0) {
        left = true;
        return;
      }
      for (const item of props.series) {
        if (resolveSeriesYAxis(item.yAxis) === 'right') {
          right = true;
        } else {
          left = true;
        }
      }
      return;
    }
    const childProps = child.props as { children?: ReactNode };
    if (childProps.children) {
      Children.forEach(childProps.children, searchChild);
    }
  };
  Children.forEach(children, searchChild);
  return { left, right };
}

function toSideConfig(
  element: YAxisElementConfig,
  seriesDataKeys: string[],
  referenceLinePoints: number[],
  extraInclusionPoints: number[] = [],
  useStackedYDomain?: boolean,
): YAxisSideConfig {
  return {
    seriesDataKeys,
    hasYAxis: element.hasYAxis,
    yAxisNumTicks: element.numTicks,
    domainInclusionPoints: [...referenceLinePoints, ...extraInclusionPoints],
    referenceLinePoints,
    yAxisFallbackMin: element.fallbackMin,
    yAxisFallbackMax: element.fallbackMax,
    yAxisDataType: element.dataType,
    yAxisTickFormat: element.tickFormat,
    domainMin: element.domainMin,
    domainMax: element.domainMax,
    yAxisLabelOffset: element.yAxisLabelOffset,
    useStackedYDomain,
  };
}

function isRightAxisActive(right: YAxisSideConfig): boolean {
  return (
    right.hasYAxis ||
    right.seriesDataKeys.length > 0 ||
    right.domainInclusionPoints.length > 0 ||
    right.domainMin !== undefined ||
    right.domainMax !== undefined
  );
}

/**
 * Extracts Y-axis configuration from chart children and reference line data.
 *
 * Walks the React children tree to find YAxis props, Series data keys,
 * ReferenceLine points, and ReferenceArea bounds. This enables unidirectional
 * data flow: the chart extracts config synchronously at initialization,
 * preventing the Y-domain flash from an initial render with the wrong scale.
 */
export function extractYAxisConfig(
  children: ReactNode,
  referenceLineData?: ReferenceLineData[],
  defaultNumTicks?: number,
): YAxisConfig {
  const { left: leftKeys, right: rightKeys } = findSeriesDataKeysByAxis(children);
  const { left: leftAxis, right: rightAxis } = findYAxisConfigs(children, defaultNumTicks);

  const refLinePoints = findHorizontalReferenceLinePointsFromChildren(children, referenceLineData);
  const leftRefPoints = refLinePoints.filter((p) => p.yAxis === 'left').map((p) => p.point);
  const rightRefPoints = refLinePoints.filter((p) => p.yAxis === 'right').map((p) => p.point);
  const refAreaBounds = findReferenceAreaBounds(children);

  const stacked = findStackedYDomainByAxis(children);

  const left = toSideConfig(leftAxis, leftKeys, leftRefPoints, refAreaBounds, stacked.left);
  const right = toSideConfig(rightAxis, rightKeys, rightRefPoints, [], stacked.right);

  return {
    ...left,
    useStackedYDomain: stacked.left,
    right: isRightAxisActive(right) ? right : undefined,
  };
}
